import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';


import Header from '../../components/ui/Header';
import ProfileHeader from './components/ProfileHeader';
import PersonalInfoSection from './components/PersonalInfoSection';
import SecuritySection from './components/SecuritySection';
import MembershipInfoSection from './components/MembershipInfoSection';
import NotificationPreferences from './components/NotificationPreferences';

const UserProfileManagement = () => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [activeSection, setActiveSection] = useState('personal');

  // Mock user data - in production, this would come from authentication context
  const [userData, setUserData] = useState({
    fullName: 'Juan Pérez García',
    email: 'juan.perez@example.com',
    phone: '+57 312 456 7890',
    idType: 'CC',
    idNumber: '1234567890',
    membershipTier: 'Emprendedor',
    membershipExpiry: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000),
    discountsUsed: 12,
    discountsLimit: 50,
    avatar: null
  });

  const [formData, setFormData] = useState({ ...userData });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    setUserData({ ...formData });
    setIsEditing(false);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  const handleCancel = () => {
    setFormData({ ...userData });
    setIsEditing(false);
  };

  const sections = [
    { id: 'personal', label: 'Información Personal', icon: 'User' },
    { id: 'security', label: 'Seguridad', icon: 'Lock' },
    { id: 'membership', label: 'Membresía', icon: 'Crown' },
    { id: 'notifications', label: 'Notificaciones', icon: 'Bell' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Message */}
        {showSuccessMessage && (
          <div className="mb-6 p-4 bg-success/10 border border-success rounded-lg flex items-center gap-3 animate-fade-in">
            <Icon name="CheckCircle" size={24} color="var(--color-success)" />
            <p className="text-success font-medium">Perfil actualizado exitosamente</p>
          </div>
        )}

        {/* Page Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/user-dashboard')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <Icon name="ArrowLeft" size={20} />
            <span className="text-sm">Volver al Panel</span>
          </button>
          <h1 className="text-3xl font-bold text-foreground mb-2">Mi Perfil</h1>
          <p className="text-muted-foreground">Gestiona tu información personal y preferencias de cuenta</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-card rounded-lg border border-border p-4 shadow-card sticky top-24">
              <nav className="space-y-2">
                {sections?.map((section) => (
                  <button
                    key={section?.id}
                    onClick={() => setActiveSection(section?.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      activeSection === section?.id
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon
                      name={section?.icon}
                      size={20}
                      color={activeSection === section?.id ? 'currentColor' : 'var(--color-muted-foreground)'}
                    />
                    <span className="text-sm font-medium">{section?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Profile Header */}
            <ProfileHeader
              userData={userData}
              isEditing={isEditing}
              onAvatarChange={(file) => handleInputChange('avatar', file)}
            />

            {/* Personal Information */}
            {activeSection === 'personal' && (
              <PersonalInfoSection
                formData={formData}
                isEditing={isEditing}
                onInputChange={handleInputChange}
              />
            )}

            {/* Security */}
            {activeSection === 'security' && (
              <SecuritySection email={userData?.email} />
            )}

            {/* Membership Information */}
            {activeSection === 'membership' && (
              <MembershipInfoSection userData={userData} />
            )}

            {/* Notification Preferences */}
            {activeSection === 'notifications' && (
              <NotificationPreferences />
            )}

            {/* Action Buttons */}
            {activeSection === 'personal' && (
              <div className="bg-card rounded-lg border border-border p-6 shadow-card">
                <div className="flex flex-col sm:flex-row gap-4">
                  {!isEditing ? (
                    <Button
                      variant="default"
                      onClick={() => setIsEditing(true)}
                      iconName="Edit"
                      iconPosition="left"
                      fullWidth
                    >
                      Editar Información
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="default"
                        onClick={handleSave}
                        iconName="Save"
                        iconPosition="left"
                        fullWidth
                      >
                        Guardar Cambios
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleCancel}
                        iconName="X"
                        iconPosition="left"
                        fullWidth
                      >
                        Cancelar
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileManagement;